/**
 * One derivation of "what does this print file still need?".
 *
 * Pure — no React, no I/O — so the dialog, the bulk modal and their tests all
 * read the SAME precedence. Before this, each surface re-derived the answer
 * from the raw matrix fields (`present` / `needsReplacement` / `locked`) and
 * disagreed: one showed amber "missing" where the other said "replacement
 * needed", and neither could name the `unknown` status the server returns
 * when it was asked NOT to probe storage (`includeDownloadUrls: false`).
 */
import { AlertTriangle, CheckCircle2, Clock, HelpCircle, Loader2 } from 'lucide-react';

/** The matrix's per-target status, verbatim from `readTargetStatus`. */
export type PrintFileTargetStatus = 'present' | 'missing' | 'replacement_needed' | 'unknown';

/** The subset of the server matrix row this component renders. */
export interface PrintFileTargetSummary {
  side: string;
  format: string;
  filename: string;
  status: PrintFileTargetStatus;
  locked: boolean;
  awaitingBridge: boolean;
  downloadUrl?: string | null;
}

/**
 * The operator's progress on one target. Shared by the dialog (per-row
 * submit) and the bulk modal (one run over every row), so the two can never
 * disagree about what "uploading" looks like.
 */
export type PrintFileTargetPhase =
  | 'idle'
  | 'uploading'
  | 'verifying'
  | 'verified'
  | 'success'
  | 'error';

export interface TargetPresentation {
  labelKey: string;
  className: string;
  Icon: typeof CheckCircle2;
  spin?: boolean;
}

/**
 * Single source of truth for a target's label/colour/icon. Precedence is
 * deliberate: machine ownership first (nothing the operator does can land
 * while the bridge holds the key), then the wait, then presence.
 */
export function describePrintFileTarget(
  target: Pick<PrintFileTargetSummary, 'status' | 'locked' | 'awaitingBridge'>,
): TargetPresentation {
  if (target.locked) {
    return {
      labelKey: 'issues.printFiles.upload.targetStatus.processing',
      className: 'text-sky-700 dark:text-sky-300',
      Icon: Loader2,
      spin: true,
    };
  }
  if (target.awaitingBridge) {
    return {
      labelKey: 'issues.printFiles.upload.targetStatus.awaitingBridge',
      className: 'text-amber-700 dark:text-amber-300',
      Icon: Clock,
    };
  }
  if (target.status === 'present') {
    return {
      labelKey: 'issues.printFiles.upload.targetStatus.present',
      className: 'text-emerald-700 dark:text-emerald-300',
      Icon: CheckCircle2,
    };
  }
  if (target.status === 'unknown') {
    return {
      labelKey: 'issues.printFiles.upload.targetStatus.unknown',
      className: 'text-muted-foreground',
      Icon: HelpCircle,
    };
  }
  return {
    labelKey:
      target.status === 'replacement_needed'
        ? 'issues.printFiles.upload.targetStatus.replacement_needed'
        : 'issues.printFiles.upload.targetStatus.missing',
    className: 'text-amber-700 dark:text-amber-300',
    Icon: AlertTriangle,
  };
}

